/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { useQuery } from '@apollo/client/react'
import { NavBadge } from '@repo/shared-client/components/blocks'
import { Badge, Button } from '@repo/shared-client/components/ui'
import { Link } from '@tanstack/react-router'
import { useTranslation } from '@web/src/lib/intl/react'
import { limitBadgeNumber } from '@web/src/lib/utilsClient'
import { MessagesSquareIcon, UsersIcon } from 'lucide-react'
import { CHAT_UNREAD_SUMMARY_QUERY } from '../chat-queries'

/**
 * The nav badge for chat.
 *
 * Polled rather than driven by realtime: the badge is visible on every page, and holding
 * an Ably connection app-wide just to keep a number current would consume a concurrent
 * connection slot for every open tab. One aggregate query a minute is far cheaper.
 *
 * The number is unread direct and group messages (`messagesBadgeCount`). Channel
 * messages stay on the channel list and do not add to it. A room at NONE still
 * contributes a bold unread name in the sidebar but no numeric badge.
 *
 * The small badge under the icon is everyone currently in any channel. It uses the
 * same poll; a person present in two channels is counted twice, matching each room's
 * presence total.
 */
export const ChatIndicator = () => {
  const { t } = useTranslation()

  const { data } = useQuery(CHAT_UNREAD_SUMMARY_QUERY, {
    fetchPolicy: 'no-cache',
    pollInterval: 60_000,
  })

  const messageBadgeCount = data?.chatUnreadSummary?.messagesBadgeCount ?? 0
  const totalBadgeCount = data?.chatUnreadSummary?.badgeCount ?? 0
  const unreadRoomCount = data?.chatUnreadSummary?.unreadRoomCount ?? 0
  const channelOnlineCount = data?.chatUnreadSummary?.channelOnlineCount ?? 0

  // Channel unreads live in the all-rooms total. They must not become the muted-room
  // dot once they are left out of the number.
  const hasMutedUnread = totalBadgeCount === 0 && unreadRoomCount > 0
  const badge = messageBadgeCount > 0 ? messageBadgeCount : hasMutedUnread ? true : undefined

  const ariaLabel = channelOnlineCount > 0 ? t('Chat, {{count}} chatting', { count: channelOnlineCount }) : t('Chat')

  return (
    <Button
      aria-label={ariaLabel}
      className="relative overflow-visible"
      nativeButton={false}
      render={<Link to="/chat" />}
      size="icon-lg"
      variant="ghost"
    >
      <MessagesSquareIcon aria-hidden className="size-7" />
      <NavBadge badge={badge} size="md" />
      {channelOnlineCount > 0 ? (
        <Badge
          aria-hidden
          className="pointer-events-none absolute -bottom-3 left-1/2 -translate-x-1/2 tabular-nums leading-none shadow-sm"
          size="sm"
          variant="secondary"
        >
          <UsersIcon />
          {limitBadgeNumber(channelOnlineCount)}
        </Badge>
      ) : null}
    </Button>
  )
}

export default ChatIndicator
